import { ChangeEvent, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import BackupRoundedIcon from "@mui/icons-material/BackupRounded";
import RestoreRoundedIcon from "@mui/icons-material/RestoreRounded";
import { downloadDatabaseBackup, restoreDatabaseBackup } from "../api/admin";
import { useNotifications } from "../components/NotificationProvider";

export function BackupRestorePage() {
  const { showAlert, showSnackbar } = useNotifications();
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [backupProgress, setBackupProgress] = useState<number | null>(null);
  const [restoreProgress, setRestoreProgress] = useState<number | null>(null);
  const [backupStatus, setBackupStatus] = useState("Preparing backup...");
  const [restoreStatus, setRestoreStatus] = useState("Uploading backup...");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleDownloadBackup = async () => {
    setBackupLoading(true);
    setBackupProgress(null);
    setBackupStatus("Preparing backup...");
    try {
      const { blob, filename } = await downloadDatabaseBackup(
        (loadedBytes, totalBytes) => {
          setBackupStatus("Downloading backup...");
          setBackupProgress(
            totalBytes > 0
              ? Math.round((loadedBytes / totalBytes) * 100)
              : null,
          );
        },
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showSnackbar({
        title: "Backup ready",
        message: "The database backup file is downloading.",
        variant: "success",
      });
    } catch (error) {
      showAlert({
        title: "Backup failed",
        message:
          error instanceof Error ? error.message : "Unable to generate backup.",
        variant: "error",
      });
    } finally {
      setBackupLoading(false);
      setBackupProgress(null);
    }
  };

  const handleRestoreFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
  };

  const handleRestoreDatabase = async () => {
    if (!selectedFile) {
      showAlert({
        title: "Select a backup file",
        message: "Please choose a PostgreSQL backup file to restore.",
        variant: "error",
      });
      return;
    }

    setRestoreLoading(true);
    setRestoreProgress(0);
    setRestoreStatus("Uploading backup...");
    try {
      await restoreDatabaseBackup(
        selectedFile,
        (loadedBytes, totalBytes, phase) => {
          setRestoreStatus(
            phase === "processing"
              ? "Restoring database..."
              : "Uploading backup...",
          );
          setRestoreProgress(
            totalBytes > 0
              ? Math.round((loadedBytes / totalBytes) * 100)
              : null,
          );
        },
      );
      showSnackbar({
        title: "Restore completed",
        message: "Database restore finished successfully.",
        variant: "success",
      });
      setSelectedFile(null);
    } catch (error) {
      showAlert({
        title: "Restore failed",
        message:
          error instanceof Error
            ? error.message
            : "Unable to restore the backup.",
        variant: "error",
      });
    } finally {
      setRestoreLoading(false);
      setRestoreProgress(null);
    }
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Backup & Restore
      </Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 680 }}>
        Create a complete backup containing the application database and all
        uploaded documents and images, or restore a previously generated backup.
        This tool is intended for administrators and should be used carefully.
      </Typography>

      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardHeader
          title="Create complete backup"
          subheader="Download the database and all uploaded files for offline storage or disaster recovery."
          titleTypographyProps={{ fontWeight: 700 }}
        />
        <Divider />
        <CardContent>
          <Stack spacing={2}>
            <Typography color="text.secondary">
              The backup includes benefit documents, images, and their database
              records. It is generated using `pg_dump` and downloaded as a dated
              `.tar.gz` archive.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                startIcon={<BackupRoundedIcon />}
                onClick={handleDownloadBackup}
                disabled={backupLoading}
                sx={{ borderRadius: 2, textTransform: "none" }}
              >
                Download backup
              </Button>
            </Box>
            {backupLoading && (
              <Stack spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  {backupStatus}
                  {backupProgress !== null ? ` ${backupProgress}%` : ""}
                </Typography>
                <LinearProgress
                  variant={
                    backupProgress === null ? "indeterminate" : "determinate"
                  }
                  value={backupProgress ?? undefined}
                />
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardHeader
          title="Restore complete backup"
          subheader="Upload a complete archive to restore the database and uploaded files."
          titleTypographyProps={{ fontWeight: 700 }}
        />
        <Divider />
        <CardContent>
          <Stack spacing={2}>
            <Typography color="text.secondary">
              Choose a `.tar.gz` backup created by this application. Older
              `.sql` database-only backups are also supported, but they cannot
              restore uploaded documents that were not included in the file.
            </Typography>
            <input
              type="file"
              accept=".tar.gz,.gz,.sql,application/gzip,application/sql"
              id="backup-file-input"
              style={{ display: "none" }}
              onChange={handleRestoreFileChange}
            />
            <label htmlFor="backup-file-input">
              <Button
                variant="outlined"
                component="span"
                sx={{ borderRadius: 2, textTransform: "none" }}
              >
                {selectedFile ? selectedFile.name : "Select backup file"}
              </Button>
            </label>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                color="error"
                startIcon={<RestoreRoundedIcon />}
                onClick={handleRestoreDatabase}
                disabled={restoreLoading || !selectedFile}
                sx={{ borderRadius: 2, textTransform: "none" }}
              >
                Restore backup
              </Button>
            </Box>
            {restoreLoading && (
              <Stack spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  {restoreStatus}
                  {restoreProgress !== null ? ` ${restoreProgress}%` : ""}
                </Typography>
                <LinearProgress
                  color="error"
                  variant={
                    restoreProgress === null ? "indeterminate" : "determinate"
                  }
                  value={restoreProgress ?? undefined}
                />
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
